import { getFirebaseAuth } from './firebase';
import { signOut } from './authService';

// ============================================================================
// TYPES
// ============================================================================

export type AuthRecoveryResult = 'recovered' | 'logged-out' | 'not-auth-error';

// Callback for when user is logged out due to auth failure
type OnAuthFailureCallback = () => void;
let onAuthFailureCallback: OnAuthFailureCallback | null = null;

// ============================================================================
// REGISTRATION
// ============================================================================

/**
 * Register a callback to be called when auth recovery fails and user is logged out.
 * This should be called once at app startup to handle redirect/toast.
 */
export function setOnAuthFailureCallback(callback: OnAuthFailureCallback): void {
  onAuthFailureCallback = callback;
}

// ============================================================================
// AUTH RECOVERY
// ============================================================================

/**
 * Handle a Firestore permission error by attempting to recover the auth session.
 *
 * @param error - The error from Firestore onSnapshot or other operations
 * @returns
 *   - 'recovered': Token was refreshed successfully, caller should resubscribe
 *   - 'logged-out': Session is dead, user has been signed out
 *   - 'not-auth-error': Error was not auth-related, no action taken
 */
export async function handleAuthError(error: unknown): Promise<AuthRecoveryResult> {
  // Check if this is a permission error
  const errorCode = (error as { code?: string })?.code;
  if (errorCode !== 'permission-denied') {
    return 'not-auth-error';
  }

  console.log('[AuthRecovery] Permission denied error detected, attempting token refresh...');

  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;

  // No user logged in
  if (!currentUser) {
    console.log('[AuthRecovery] No current user, signing out...');
    await signOut();
    onAuthFailureCallback?.();
    return 'logged-out';
  }

  try {
    // Force token refresh
    await currentUser.getIdToken(true);
    console.log('[AuthRecovery] Token refreshed successfully');
    return 'recovered';
  } catch (refreshError) {
    // Token refresh failed - session is truly dead
    console.error('[AuthRecovery] Token refresh failed, signing out...', refreshError);
    await signOut();
    onAuthFailureCallback?.();
    return 'logged-out';
  }
}
